import * as types from "./actionTypes";
import projectApi from "../api/projectApi";
import geographyApi from "../api/geographyApi";
import * as ajax from "./ajaxCounterActions";
import * as exception from "./exceptionActions";
import * as constants from "../constants/DomainConstants";
import * as beads from "./beadActions";

// ----------------------------------------------------------------------------
export function projectLoaded(project, errors, hasValidationErrors, isSaved = true) {
	return {
		type: types.PROJECT_LOADED,
		body: {
			item: project,
			errors: errors,
			isSaved: isSaved,
			hasValidationErrors: hasValidationErrors
		}
	};
}

export function projectUnloaded() {
	return {
		type: types.PROJECT_UNLOADED
	};
}

export function infrastructurenInDeBuurtLoaded(infrastructurenModel, gemeente, straat, postcode, inEditMode, origProject = null) {
	return {
		type: types.INFRASTRUCTUREN_LOADED,
		body: {
			item: infrastructurenModel,
			gemeente: gemeente,
			straat: straat,
			postcode: postcode,
			project: origProject,
			inEditMode: inEditMode
		}
	};
}

export function loadProject(dossierId) {
	return function (dispatch) {
		dispatch(ajax.started());

		return projectApi.get(dossierId)
			.then(response => {
				dispatch(ajax.ended());
				dispatch(projectLoaded(response.data.item, response.data.validationResults, response.data.hasValidationErrors));
				dispatch(beads.beadsLoaded(response.data.item.beadModels));


				const result = response.data.item;
				const lengtegraad = result.lengtegraad;
				const breedtegraad = result.breedtegraad;
				const gemeente = result.gemeente;
				const straat = result.straatHuisnummer;
				const postcode = result.postcode;
				if (lengtegraad && breedtegraad) {
					dispatch(getInfrastructurenInDeBuurt(lengtegraad, breedtegraad, gemeente, straat, postcode));
				}

				const { hash } = window.location;
				if (hash !== '') {
					// Push onto callback queue so it runs after the DOM is updated,
					// this is required when navigating from a different page so that
					// the element is rendered on the page before trying to getElementById.
					setTimeout(() => {
						const id = hash.replace('#', '');
						const element = document.getElementById(id);
						if (element) element.scrollIntoView();
					}, 0);
				}
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}

export function unloadProject() {
	return function (dispatch) {
		dispatch(projectUnloaded());
	};
}

export function getInfrastructurenInDeBuurt(lengtegraad, breedtegraad, gemeente, straat, postcode, inEditMode, origProject = null) {
	return function (dispatch) {
		dispatch(ajax.started());

		return projectApi.getInfrastructurenInDeBuurt(lengtegraad, breedtegraad)
			.then(response => {
				dispatch(ajax.ended());
				dispatch(infrastructurenInDeBuurtLoaded(response.data, gemeente, straat, postcode, inEditMode, origProject));
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}

// ----------------------------------------------------------------------------
export function projectSaved(project, errors, hasValidationErrors) {
	return {
		type: types.PROJECT_SAVED,
		body: {
			item: project,
			errors: errors,
			hasValidationErrors: hasValidationErrors
		}
	};
}

export function saveProject(project) {
	return function (dispatch) {
		dispatch(ajax.started());

		return projectApi.save(project)
			.then((response) => {
				dispatch(ajax.ended());

				if (response.data.concurrencyException) {
					dispatch(projectSaved(response.data.item, response.data.validationResults, response.data.hasValidationErrors));
					reloadInfrastructurenInDeBuurt(response.data.item, dispatch);
					return constants.HAS_CONCURRENCY_EXCEPTION;
				}
				if (response.data.hasValidationErrors) {
					dispatch(projectSaved(response.data.item, response.data.validationResults, response.data.hasValidationErrors));
					reloadInfrastructurenInDeBuurt(response.data.item, dispatch);
					return constants.HAS_VALIDATION_ERRORS;
				}

				if (Object.keys(response.data.validationResults).length !== 0) {
					//Necessary to have the validation on the correct item in the current working list of infrastructuren.
					//We have no way of sorting the list in the page and on the server the same way without using a tempId going to the server and back.
					// => decided to do this 'hack' instead of spending a lot of time on doing it the 'right' way
					//No risk of lost server data being ignored, since a concurrency error is thrown if this happens.
					const workingInfrastructuren = project.infrastructuren;
					const updatedModelFromServer = Object.assign({}, response.data.item, { infrastructuren: workingInfrastructuren });
					dispatch(projectSaved(updatedModelFromServer, response.data.validationResults, response.data.hasValidationErrors));
					reloadInfrastructurenInDeBuurt(response.data.item, dispatch);
					dispatch(beads.beadsLoaded(response.data.item.beadModels));
					return constants.HAS_VALIDATION_ERRORS_SAVED;
				}

				dispatch(projectSaved(response.data.item, {}, false));
				dispatch(beads.beadsLoaded(response.data.item.beadModels));

				reloadInfrastructurenInDeBuurt(response.data.item, dispatch);

				return constants.SUCCESS;
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}

function reloadInfrastructurenInDeBuurt(result, dispatch) {
	const lengtegraad = result.lengtegraad;
	const breedtegraad = result.breedtegraad;
	const gemeente = result.gemeente;
	const straat = result.straatHuisnummer;
	const postcode = result.postcode;
	if (lengtegraad && breedtegraad) {
		dispatch(getInfrastructurenInDeBuurt(lengtegraad, breedtegraad, gemeente, straat, postcode));
	}
}

// ----------------------------------------------------------------------------
//'get' because the result is directly returned to the requestor
//no action is dispatched and reduced, and the result is not pushed to the store. load = load it into the store
export function getGeoCoordinates(adres) {
	return (dispatch) => {
		dispatch(ajax.started());

		return geographyApi.getCoordinatenForAdres(adres)
			.then(response => {
				dispatch(ajax.ended());
				return response.data;
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}

// ----------------------------------------------------------------------------
//'get' because the result is directly returned to the requestor
//no action is dispatched and reduced, and the result is not pushed to the store. load = load it into the store
export function getEmptyInfrastructuur() {
	return (dispatch) => {
		dispatch(ajax.started());
		return projectApi.loadEmptyInfrastructuur()
			.then(response => {
				dispatch(ajax.ended());
				return response.data;
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}

// ----------------------------------------------------------------------------
export function infrastructuurSaved(infrastructuur, isReset) {
	return {
		type: types.INFRASTRUCTUUR_SAVED,
		body: {
			item: infrastructuur,
			isReset: isReset
		}
	};
}

export function infrastructuurValidated(validationErrors) {
	return {
		type: types.INFRASTRUCTUUR_VALIDATED,
		body: {
			errors: validationErrors
		}
	};
}

export function simulatieInfrastructuurRemoved(infrastructuurId) {
	return {
		type: types.INFRASTRUCTUUR_REMOVED,
		body: {
			item: infrastructuurId
		}
	};
}

export function resetInfrastructuurErrors(infrastructuur) {
	return (dispatch) => {
		dispatch(infrastructuurValidated(null));
		dispatch(infrastructuurSaved(infrastructuur, true));
	};
}

export function removeInfrastructuur(infrastructuurId) {
	return (dispatch) => {
		dispatch(infrastructuurValidated(null));
		dispatch(simulatieInfrastructuurRemoved(infrastructuurId));
	};
}

export function saveInfrastructuur(infrastructuur) {
	return (dispatch) => {
		dispatch(ajax.started());
		return projectApi.validateInfrastructuur(infrastructuur)
			.then(response => {
				dispatch(ajax.ended());
				if (Object.keys(response.data.validationResults).length !== 0) {
					dispatch(infrastructuurValidated(response.data.validationResults));
					return constants.HAS_VALIDATION_ERRORS;
				}
				else {
					dispatch(infrastructuurSaved(infrastructuur, false));
					return constants.SUCCESS_NO_TOASTR;
				}
			})
			.catch(error => {
				dispatch(ajax.failed(error));
				dispatch(exception.throwException(error));
			});
	};
}



